import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Key, CheckCircle2, Loader2, AlertCircle, FileText, Download } from "lucide-react";
import api, { downloadHandVote } from "../lib/api";

export default function HandVoteCompletePage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestInfo, setRequestInfo] = useState(null);
  const [entityKeyPart, setEntityKeyPart] = useState("");
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    api.get(`/scrutiny/request/${token}`)
      .then(res => {
        setRequestInfo(res.data);
        if (res.data.status === 'pending') toast.info(t('handVote.adminNotApproved'));
        else if (res.data.status === 'completed') toast.info(t('handVote.alreadyCompleted'));
      })
      .catch(() => toast.error(t('handVote.requestNotFound')))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    if (!entityKeyPart.trim()) {
      toast.error(t('handVote.enterKey'));
      return;
    }
    setSubmitting(true);
    setErrorMsg("");
    try {
      const response = await api.post("/hand-vote/complete", { token, entity_key_part: entityKeyPart.trim() });
      setResult(response.data);
      toast.success(t('handVote.fileGenerated'));
    } catch (error) {
      setErrorMsg(error.response?.data?.detail || t('errors.fileGeneration'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.consulta_id) return;
    try {
      const res = await downloadHandVote(result.consulta_id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      link.setAttribute('download', filenameMatch ? filenameMatch[1] : `vot_ma_alcada_${requestInfo?.consulta_title?.replace(/ /g, '_') || 'consulta'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('handVote.fileDownloaded'));
    } catch {
      toast.error(t('handVote.fileDownloadError'));
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber-600" /></div>;
  if (!requestInfo) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" /></div>;

  if (result) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle2 className="h-10 w-10 text-green-600" /></div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('handVote.fileGenerated', 'Fitxer Generat')}</h1>
          <p className="text-slate-500 mb-4">{result.message}</p>
          <Button onClick={handleDownload} className="bg-amber-600 hover:bg-amber-700 mb-4" data-testid="hand-vote-download-result-btn">
            <Download className="h-4 w-4 mr-2" />
            {t('common.download')} XLSX ({result.total_records} {t('censusPage.recordsAdded', 'registres')})
          </Button>
          <Button variant="outline" onClick={() => navigate(`/consultes/${result.consulta_id}/results`)} className="w-full">
            {t('results.title', 'Anar als resultats')}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4"><FileText className="h-8 w-8 text-amber-600" /></div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1">{t('handVote.generateXlsxTitle', 'Generar XLSX - Vot a Mà Alçada')}</h1>
          <p className="text-slate-500">{t('handVote.enterKeyToComplete', 'Introdueix la teva clau per completar la generació')}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-amber-800"><strong>{t('common.consulta')}:</strong> {requestInfo.consulta_title}</p>
          <p className="text-sm text-amber-800"><strong>{t('common.entity')}:</strong> {requestInfo.entity_name}</p>
          <p className="text-sm text-amber-800"><strong>{t('common.status')}:</strong> {
            requestInfo.status === 'admin_approved' ? t('handVote.approvedByAdmin') :
            requestInfo.status === 'pending' ? t('handVote.pendingApproval') :
            requestInfo.status === 'completed' ? t('handVote.alreadyCompleted') : requestInfo.status
          }</p>
        </div>
        {requestInfo.status === 'admin_approved' ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('handVote.entityKeyLabel', 'Part de la clau privada de l\'entitat')}</label>
              <Textarea value={entityKeyPart} onChange={(e) => setEntityKeyPart(e.target.value)} placeholder={t('handVote.enterKey')} rows={5} className="font-mono text-xs" data-testid="entity-hand-vote-key-input" />
            </div>
            {errorMsg && <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{errorMsg}</div>}
            <Button onClick={handleSubmit} disabled={submitting || !entityKeyPart.trim()} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="entity-hand-vote-complete-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />}
              {t('handVote.generateAndDownloadXlsx', 'Generar i Descarregar XLSX')}
            </Button>
          </div>
        ) : requestInfo.status === 'pending' ? (
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <Loader2 className="h-6 w-6 animate-spin text-yellow-600 mx-auto mb-2" />
            <p className="text-sm text-yellow-800">{t('handVote.adminNotApprovedWait', 'L\'administrador encara no ha aprovat la sol·licitud. Rebràs un email quan estigui disponible.')}</p>
          </div>
        ) : (
          <p className="text-center text-slate-500">{t('handVote.alreadyProcessed', 'Aquesta sol·licitud ja ha estat processada.')}</p>
        )}
      </motion.div>
    </div>
  );
}
